/**
 * deadwood.ts — fallen LOGS and STUMPS for the forest floor (2026-09-25, visual-realism R6). Procedural.
 *
 * A wood floor without deadwood reads as a planted park: every real stand carries fallen trunks
 * (10–30 per ha) and stumps (20–40 per ha). There are no such assets, so they are built here, like
 * the rocks: a LOG is a bent, bark-bumped cylinder along +X (length 1, radius 1 at scale 1 — the
 * instance scales it to 3–7 m × 0.2–0.45 m) with rough end caps; a STUMP a flared cylinder (radius 1,
 * height 1) with a root foot and a cut top. ~140 / ~110 triangles. The BARK material streaks its
 * noise along the trunk axis (geometry space) and shows pale growth RINGS on the cut faces (picked by
 * the geometry normal — MEASURE positionGeometry/normalGeometry).
 */
import * as THREE from 'three/webgpu';
import { Fn, float, vec3, mix, smoothstep, positionGeometry, normalGeometry, mx_noise_float, fract, length, abs, normalWorld, vec2 } from 'three/tsl';
import { noise3, rng } from './rock_shape.js';

export type DeadwoodKind = 'log' | 'stump';
export const DEADWOOD_VARIANTS: Record<DeadwoodKind, number> = { log: 4, stump: 3 };

function build(kind: DeadwoodKind, variant: number): THREE.BufferGeometry {
  const r = rng(0x51f15e ^ (variant * 7919 + (kind === 'log' ? 0 : 104729)));
  const seed = Math.floor(r() * 1e6);
  const radial = 10;
  const pos: number[] = []; const idx: number[] = [];
  if (kind === 'log') {
    const segs = 6; const bend = (r() - 0.5) * 0.35, taper = 0.75 + 0.2 * r();
    for (let s = 0; s <= segs; s++) {
      const t = s / segs, x = t - 0.5;
      const rad = 1 - (1 - taper) * t;
      const by = Math.sin(t * Math.PI) * bend;
      for (let k = 0; k < radial; k++) {
        const a = (k / radial) * Math.PI * 2;
        const bump = 1 + noise3(x * 6, Math.cos(a) * 2, Math.sin(a) * 2, seed) * 0.12;
        pos.push(x, Math.cos(a) * rad * bump + by, Math.sin(a) * rad * bump);
      }
    }
    for (let s = 0; s < segs; s++) for (let k = 0; k < radial; k++) {
      const a = s * radial + k, b = s * radial + ((k + 1) % radial), c = (s + 1) * radial + ((k + 1) % radial), d = (s + 1) * radial + k;
      idx.push(a, d, c, a, c, b);
    }
    // end caps (a centre vertex per end, slightly domed)
    for (const [s, sign] of [[0, -1], [segs, 1]] as const) {
      const ci = pos.length / 3; const t = s / segs;
      pos.push(t - 0.5 + sign * 0.02, Math.sin(t * Math.PI) * bend, 0);
      for (let k = 0; k < radial; k++) { const a = s * radial + k, b = s * radial + ((k + 1) % radial); if (sign > 0) idx.push(ci, a, b); else idx.push(ci, b, a); }
    }
  } else {
    const rings = 4; const hgt = 1;
    for (let s = 0; s <= rings; s++) {
      const t = s / rings, y = t * hgt;
      const flare = 1 + 0.55 * Math.pow(1 - t, 3);
      for (let k = 0; k < radial; k++) {
        const a = (k / radial) * Math.PI * 2;
        const root = 1 + (s === 0 ? 0.25 * Math.max(0, Math.sin(a * 3 + r() * 6)) : 0);
        const bump = 1 + noise3(Math.cos(a) * 2, y * 4, Math.sin(a) * 2, seed) * 0.1;
        const topJag = s === rings ? noise3(Math.cos(a) * 3, 9, Math.sin(a) * 3, seed) * 0.12 : 0;
        pos.push(Math.cos(a) * flare * root * bump, y + topJag, Math.sin(a) * flare * root * bump);
      }
    }
    for (let s = 0; s < rings; s++) for (let k = 0; k < radial; k++) {
      const a = s * radial + k, b = s * radial + ((k + 1) % radial), c = (s + 1) * radial + ((k + 1) % radial), d = (s + 1) * radial + k;
      idx.push(a, d, c, a, c, b);
    }
    const ci = pos.length / 3; pos.push(0, hgt + 0.02, 0);
    for (let k = 0; k < radial; k++) { const a = rings * radial + k, b = rings * radial + ((k + 1) % radial); idx.push(ci, b, a); }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals(); g.computeBoundingBox(); g.computeBoundingSphere();
  g.name = `deadwood:${kind}:${variant}`;
  return g;
}

const cache = new Map<string, THREE.BufferGeometry>();
export function deadwoodGeometry(kind: DeadwoodKind, variant: number): THREE.BufferGeometry {
  const n = DEADWOOD_VARIANTS[kind]; const v = ((variant % n) + n) % n; const k = `${kind}:${v}`;
  let g = cache.get(k); if (!g) { g = build(kind, v); cache.set(k, g); } return g;
}
/** A box-ish collider for a log at unit scale (the instance transform is baked by the caller). */
export function deadwoodColliderMesh(kind: DeadwoodKind, variant: number): { positions: number[]; indices: number[] } {
  const g = deadwoodGeometry(kind, variant);
  return { positions: Array.from(g.getAttribute('position').array as Float32Array), indices: Array.from(g.index!.array as ArrayLike<number>) };
}

const barkMats = new Map<DeadwoodKind, THREE.MeshStandardNodeMaterial>();
/** The bark / cut-wood material per kind (a log's axis is X, a stump's Y — the fibre streaks along it). */
export function barkMaterial(kind: DeadwoodKind = 'log'): THREE.MeshStandardNodeMaterial {
  const hit = barkMats.get(kind); if (hit) return hit;
  const m = new THREE.MeshStandardNodeMaterial();
  m.name = `deadwood-bark-${kind}`;
  const pg = positionGeometry, ng = normalGeometry;
  const fibre = (kind === 'log' ? mx_noise_float(pg.mul(vec3(1.2, 14, 14)), 1, 0) : mx_noise_float(pg.mul(vec3(14, 1.4, 14)), 1, 0)).mul(0.5).add(0.5);
  const cut = kind === 'log' ? smoothstep(float(0.8), float(0.95), abs(ng.x)) : smoothstep(float(0.8), float(0.95), ng.y);
  const rings = kind === 'log' ? fract(length(vec2(pg.y, pg.z)).mul(7.5)) : fract(length(vec2(pg.x, pg.z)).mul(7.5));
  m.colorNode = Fn(() => {
    const bark = mix(vec3(0.075, 0.058, 0.045), vec3(0.16, 0.13, 0.1), fibre);
    const moss = mix(bark, vec3(0.09, 0.14, 0.06), smoothstep(float(0.55), float(0.9), normalWorld.y).mul(0.6));
    const wood = mix(vec3(0.34, 0.26, 0.17), vec3(0.24, 0.17, 0.1), smoothstep(float(0.35), float(0.6), rings));
    return mix(moss, wood, cut);
  })();
  m.roughnessNode = mix(float(0.92), float(0.78), cut);
  m.metalnessNode = float(0);
  barkMats.set(kind, m);
  return m;
}
